/**
 * ONE WORD FOR WHERE A THING STANDS (22 Sep).
 *
 * An order used to wear two pills — its status ("approved") and its
 * fulfilment ("partly dispatched") — and a challan wore its status and its
 * journey. Two words for one row is one word too many for the person reading
 * the register: he wants to know what to DO with it. These two functions turn
 * the facts the views publish into the single office word every screen shows,
 * so that ten screens cannot say it ten ways.
 */
import { words } from './format.js';
import type { Tone } from './ui.js';

export interface Standing { word: string; tone: Tone }

/** An order, from its status and its fulfilment; an amended-away version says so. */
export function orderStanding(status: string, fulfilment: string, supersededById?: number | null): Standing {
  if (status === 'superseded' || (supersededById !== undefined && supersededById !== null)) return { word: 'Amended', tone: 'testing' };
  if (status === 'draft') return { word: 'Waiting for approval', tone: 'empty' };
  if (status === 'cancelled' || fulfilment === 'cancelled') return { word: 'Cancelled', tone: 'danger' };
  if (status === 'approved') {
    switch (fulfilment) {
      case 'delivered': return { word: 'Delivered', tone: 'good' };
      case 'dispatched': return { word: 'Sent', tone: 'good' };
      case 'partly_dispatched': return { word: 'Partly sent', tone: 'primary' };
      default: return { word: 'Approved, nothing sent', tone: 'primary' };
    }
  }
  return { word: words(status), tone: 'muted' };
}

/**
 * ONE WORD FOR A SHIPMENT, AND THE NEXT THING TO DO WITH IT (R20261005-07).
 *
 * The owner's review: *"Related screens use 'Sent', 'On the road', 'Departure' and 'Delivery'.
 * Dispatch filters say 'Approved' while rows distinguish 'Ready to leave' and 'On the road'.
 * These labels require operators to translate between stages and screens."* He is right, and
 * the translation was being done in the operator's head on every screen.
 *
 * So one function answers both halves — WHERE IT STANDS and WHAT HAPPENS NEXT — and every
 * screen that shows a challan asks it. A filter and a row cannot disagree about a load when
 * the words come from the same place.
 *
 * TWO FACTS, NOT ONE. `status` is the paperwork (being checked, approved, rejected, reversed)
 * and `journey` is the lorry (standing, departed, delivered). They are deliberately separate
 * in the data and they are folded only for READING, which is the same rule `whereItIs` keeps.
 *
 * AND OPEN SHORTAGES ARE PART OF THE ANSWER (R20261005-08). A load every line of which has an
 * outcome is not a finished load if one of those outcomes was "nobody can find it": the
 * delivery screen said "Nothing is waiting on this delivery" directly above the cylinders
 * nobody could account for. A shortage IS an outcome, so "every line decided" and "nothing
 * outstanding" are two different questions, and conflating them is what produced the
 * contradiction. `openShortages` is the second question, asked here so that every screen
 * showing the load gets the same answer.
 */
export interface ShipmentStanding extends Standing {
  /** The one thing to do with it now, in the office's words. Empty when nothing is owed. */
  next: string;
}

export function shipmentStanding(
  type: string, status: string, journey: string | null | undefined, openShortages = 0,
): ShipmentStanding {
  if (status === 'pending') {
    return { word: 'Being checked', tone: 'empty',
             next: type === 'return' ? 'Approve the return' : 'Approve the load' };
  }
  if (status === 'rejected') return { word: 'Sent back for correction', tone: 'danger', next: 'Correct it and submit again' };
  if (status === 'reversed') return { word: 'Reversed', tone: 'danger', next: '' };

  if (status === 'approved') {
    if (journey === 'departed') {
      // THE STAGE THE OWNER SAID WAS HARD TO NAVIGATE OUT OF. It says the next action in the
      // same words as the button that performs it, so there is nothing to translate.
      return { word: 'Out for delivery', tone: 'primary', next: 'Confirm delivery' };
    }
    if (journey === 'delivered' || journey === 'closed') {
      if (openShortages > 0) {
        return { word: 'Delivered — cylinders unaccounted for', tone: 'danger',
                 next: `Account for ${openShortages} cylinder${openShortages === 1 ? '' : 's'}` };
      }
      return { word: 'Delivered', tone: 'good', next: '' };
    }
    // the journey is null or 'approved' while the load still stands at the plant
    if (!journey || journey === 'approved') {
      return type === 'return'
        ? { word: 'Received', tone: 'good', next: '' }
        : { word: 'Ready to leave', tone: 'good', next: 'Record the departure' };
    }
    return { word: words(journey), tone: 'muted', next: '' };
  }
  return { word: words(journey ?? status), tone: 'muted', next: '' };
}

/**
 * The same answer without the next action, for the callers that only show a pill.
 * Kept as its own name so a caller that wants the word alone does not have to know about
 * shortages — but it is the SAME function underneath, which is the whole point.
 */
export function challanStanding(type: string, status: string, journey: string | null | undefined): Standing {
  const s = shipmentStanding(type, status, journey);
  return { word: s.word, tone: s.tone };
}

/**
 * WHAT A SELF-CHECK FOUND, IN WORDS (26 Sep).
 *
 * A check is NAMED for the rule it enforces — `a_document_number_matches_the_
 * calendar` — because that is what reads well in the code. A finding is the
 * rule BROKEN, so printing the check's name at a person tells him the opposite
 * of what happened. These are the failures, in the office's words.
 *
 * Lived in `home.tsx` with two entries while Home was the only place a finding
 * was shown; the full queue now has a screen, and the same words belong on
 * both. An unknown name falls back to itself with its underscores opened out,
 * so a new check is readable on the day it is written rather than invisible.
 */
const FINDING_WORDS: Record<string, string> = {
  audit_chain_intact: 'history has been changed outside the application',
  position_matches_movement_book: 'where the register says it is does not match its movements',
  ledger_balance_matches: 'the stored balance does not match the ledger',
  customer_balance_matches_the_ledger: 'the stored balance does not match the ledger',
  one_open_lending_per_cylinder: 'two trips are open for one cylinder at once',
  one_live_dispatch_per_cylinder: 'one cylinder is out on two live dispatches',
  no_stale_approvals: 'a load has been waiting too long for somebody to approve it',
  unique_document_numbers: 'two documents share one number',
  a_site_belongs_to_the_customer_named: "a record names another customer's site",
  every_record_names_somebody_real: 'a record names a customer or site that does not exist',
  a_delivery_is_not_dated_before_its_departure: 'a delivery is dated before the truck left',
  a_trip_does_not_start_before_its_dispatch: 'a trip started before its dispatch left the gate',
  the_movement_book_runs_forwards: 'a cylinder was delivered before it was despatched',
  a_live_load_agrees_with_the_register: 'a cylinder still out is not where the dispatch says it is',
  an_open_trip_means_the_customer_has_it: 'a trip is open but the cylinder is not with the customer',
  a_cylinder_at_a_customer_is_on_a_trip: 'a cylinder is at a customer with nobody being charged rent',
  a_cost_bill_was_due_before_it_was_raised: 'a replacement cost was charged before it was due',
  a_document_number_matches_the_calendar: 'a document number does not match the calendar',
  a_document_asked_for_was_produced: 'a document somebody asked for was never produced',
  every_numbered_document_can_be_dated: 'a numbered document could not be checked against the calendar',
  the_document_check_sees_every_document: 'the document check did not look at every numbered document',
};
export function findingWords(check: string): string {
  return FINDING_WORDS[check] ?? check.replace(/_/g, ' ');
}

/**
 * THE WORDS A CHALLAN'S STANDING CAN BE FILTERED BY (audit L4).
 *
 * `challanStanding()` below turns a challan's facts into the word its row
 * shows; these are the same words offered as a filter, and each names the
 * server-side predicate (`standing` on the `challans.list` view) that selects
 * them. The view holds the SQL and this holds the vocabulary, deliberately:
 * the UI must not import a view module, which would pull the server's own
 * imports into the browser bundle. `tests/ui/standing.test.ts` fails if the
 * two ever disagree, so the split cannot become a drift.
 */
export const STANDING_WORDS = [
  { value: 'being_checked', label: 'Being checked' },
  { value: 'ready_to_leave', label: 'Ready to leave' },
  { value: 'received', label: 'Received' },
  { value: 'on_the_road', label: 'Out for delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'rejected', label: 'Sent back for correction' },
  { value: 'reversed', label: 'Reversed' },
] as const;
export type StandingWord = (typeof STANDING_WORDS)[number]['value'];

/**
 * WHERE A CYLINDER IS, IN ONE PHRASE (audit Y1/Y2, 26 Sep).
 *
 * The register printed three columns — a raw legacy state (`filled_in_plant`),
 * a raw holder (`customer:5`) and a day count — and left the reader to join
 * them into the sentence he actually wanted. The facts stay separate in the
 * data, exactly as ADR-008/D264 require; what is folded is the READING of
 * them, in the one place every screen shares, so ten screens cannot phrase it
 * ten ways.
 *
 * `holderName` is the name the view resolved; a holder with no name falls back
 * to the encoding rather than going silent, because a cylinder with somebody
 * must never read as a cylinder with nobody.
 */
export interface Whereabouts { word: string; tone: Tone; note: string | null }
export function whereItIs(
  state: string | null,
  holder: string | null,
  holderName: string | null,
  daysOut?: number | null,
): Whereabouts {
  const who = holderName ?? (holder && holder !== 'plant' ? holder : null);
  const out = daysOut === undefined || daysOut === null ? null : `${daysOut} day${daysOut === 1 ? '' : 's'} out`;
  switch (state) {
    case 'filled_in_plant': return { word: 'Filled, in our plant', tone: 'good', note: 'ready to go out' };
    // D248 — a return is recorded empty whether it came back empty or part
    // full. That is a rule about rent, not a measurement, and the phrase says
    // so rather than implying the vessel is known to be safe to fill.
    case 'empty_in_plant': return { word: 'Recorded empty, in our plant', tone: 'empty', note: 'a commercial record, not a measurement' };
    case 'in_transit': return { word: who ? `On the road to ${who}` : 'On the road', tone: 'primary', note: 'ours until he takes it' };
    case 'at_customer': return { word: who ? `With ${who}` : 'With a customer', tone: 'primary', note: out };
    case 'at_owner': return { word: who ? `Back with its owner, ${who}` : "Back with its owner", tone: 'testing', note: null };
    case 'in_testing': return { word: 'At the testing station', tone: 'testing', note: null };
    case 'in_repair': return { word: 'Under repair', tone: 'testing', note: null };
    case 'unaccounted': return { word: 'Nobody can say where it is', tone: 'danger', note: 'resolve it or declare it lost' };
    case null: case undefined: case '': return { word: 'Never placed', tone: 'danger', note: 'in the register and nowhere else — find it and post where it is' };
    default: return { word: words(state), tone: 'muted', note: who ? `with ${who}` : null };
  }
}

const ACTIONS: Record<string, string> = {
  'order.create': 'Order taken', 'order.approve': 'Approved', 'order.cancel': 'Cancelled', 'order.amend': 'Quantities changed',
  'order.allocate': 'Cylinder set aside', 'order.release': 'Cylinder put back',
  'challan.create': 'Load prepared', 'challan.createDispatch': 'Load prepared', 'challan.createReturn': 'Return recorded',
  'challan.approve': 'Approved', 'challan.reject': 'Rejected', 'challan.reverse': 'Reversed', 'challan.edit_lines': 'Load corrected',
  'challan.editPendingLines': 'Load corrected', 'challan.addPendingCharge': 'Charge added', 'challan.editPendingReturn': 'Condition corrected',
  'dispatch.depart': 'Truck left', 'dispatch.deliver': 'Delivered', 'dispatch.confirmDelivery': 'Delivery confirmed',
  'dispatch.resolveShortage': 'Shortage settled', 'delivery.confirm': 'Delivery confirmed',
  'cylinder.register': 'Registered', 'cylinder.correct': 'Details corrected', 'cylinder.transfer_owner': 'Ownership transferred',
  'cylinder.convert_gas': 'Gas converted', 'cylinder.end_life': 'Taken out of service', 'damage.flag': 'Damage recorded',
  'tender.notice': 'Notice recorded', 'tender.move': 'Moved on', 'tender.bid': 'Bid recorded', 'tender.correct': 'Notice corrected',
};

/** A recorded action, in the office's words: "challan.approve" is "Approved". */
export function actionWords(action: string | null | undefined): string {
  if (!action) return '—';
  const known = ACTIONS[action];
  if (known) return known;
  const tail = action.includes('.') ? action.slice(action.indexOf('.') + 1) : action;
  const plain = tail.replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return plain.charAt(0).toUpperCase() + plain.slice(1);
}
